import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import {
  vaultResumeFounder, vaultCheck, vaultLogout, vaultListDocuments,
  vaultUploadDocument, vaultDownloadDocument, vaultDeleteDocument, formatApiError, vaultApi,
} from "@/lib/api";
import DocumentInventory from "@/components/DocumentInventory";
import {buildDocumentGroups,filterDocumentGroups} from "@/lib/documentGroups";
import CounselRelease from "@/components/CounselRelease";
import VaultRecovery from "@/components/VaultRecovery";
import { Seo } from "@/components/Seo";

const ALLOWED = ["pdf", "docx", "xlsx", "csv", "txt", "png", "jpg", "jpeg", "webp"];
const MAX_BATCH = 25;
const MAX_BYTES = 25 * 1024 * 1024;
const MAX_BATCH_BYTES = 150 * 1024 * 1024;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso || "—"; }
};
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const inputCls =
  "w-full bg-transparent border border-[rgba(180,139,63,0.35)] px-4 py-3 text-[var(--bone)] font-serif2 text-lg placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--gold)] transition-colors";

export default function SecureIntake() {
  const [authed, setAuthed] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState([]);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [queue, setQueue] = useState([]);
  const uploadLock = useRef(false);
  const progressPanel = useRef(null);
  const [inventory, setInventory] = useState(null);
  const [business, setBusiness] = useState("all");
  const [catalog, setCatalog] = useState({types: [], checklists: {}});
  const [folder, setFolder] = useState("All documents");
  const [search, setSearch] = useState("");
  const [checklist, setChecklist] = useState("Business records");
  const [excluded, setExcluded] = useState([]);
  const [organizing, setOrganizing] = useState(false);
  const category = (d) => d.legal_category || "Needs review";
  const folders = [...new Set(docs.map(category))].sort();
  const allGroups = buildDocumentGroups(docs);
  const displayDocs = filterDocumentGroups(allGroups,{folder,business,search});
  const updateQueue = (index, patch) => setQueue(rows => rows.map((row, i) => i === index ? {...row, ...patch} : row));
  const [busyId, setBusyId] = useState(null);
  const organize = async (d) => {
    setBusyId(d.file_id);
    try {
      await vaultApi.patch(`/legal/documents/${encodeURIComponent(d.file_id)}/organization`, {
        shared: !!d.legal_shared, matter: d.legal_matter || "Business records",
        category: d.legal_category || "Needs review", document_type: d.document_type || "unknown",
      });
      toast.success("Document type, folder and counsel access saved.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const editOrganization = (id, key, value) => setDocs((rows) => rows.map((d) => d.file_id === id ? { ...d, [key]: value } : d));

  const connectVault = async () => {
    setAuthed(null);
    try {
      try { await vaultCheck(); }
      catch (e) { if (e.response?.status !== 401) throw e; await vaultResumeFounder(); }
      setAuthed(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not open your document session.")); setAuthed(false); }
  };
  useEffect(() => { connectVault(); }, []);

  const load = async () => {
    setLoading(true);
    try { setDocs(await vaultListDocuments()); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (authed) { load(); vaultApi.get("/admin/documents/catalog").then(({data}) => {setCatalog(data); setChecklist(data.checklist || "Business records"); setExcluded(data.excluded || []);}).catch(() => toast.error("The document checklist could not load. Try refreshing.")); } }, [authed]);
  useEffect(() => {
    const warn = e => {e.preventDefault(); e.returnValue = "Uploads are still in progress.";};
    if (uploading || organizing) window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploading, organizing]);
  useEffect(() => {
    if (uploading || organizing) progressPanel.current?.scrollIntoView?.({behavior: "smooth", block: "start"});
  }, [uploading, organizing]);
  const saveChecklist = async (nextChecklist, nextExcluded) => {
    try {await vaultApi.post("/admin/documents/checklist", {checklist: nextChecklist, excluded: nextExcluded}); setChecklist(nextChecklist); setExcluded(nextExcluded);}
    catch (e) {toast.error(formatApiError(e.response?.data?.detail, "Checklist change could not be saved."));}
  };
  const loadInventory = async () => {
    const {data} = await vaultApi.get("/admin/documents/inventory");
    if (data?.summary) setInventory(data);
  };
  const organizeExisting = async () => {
    setOrganizing(true);
    try {await vaultApi.post("/admin/documents/organize-all"); await loadInventory(); toast.success("The entire vault is queued for background organization.");}
    catch(e) {toast.error(formatApiError(e.response?.data?.detail,"Organization could not start."));}
    finally {setOrganizing(false);}
  };
  useEffect(() => {
    if (!authed) return;
    let alive = true;
    const refresh = async () => {
      try {
        const {data} = await vaultApi.get("/admin/documents/inventory");
        if (!alive || !data?.summary) return;
        setInventory(data);
        if (!uploadLock.current) {const next = await vaultListDocuments(); if(alive) setDocs(next);}
      } catch (_) { /* Existing uploads remain usable while a background request retries. */ }
    };
    refresh(); const timer = setInterval(refresh, 10000);
    return () => {alive=false; clearInterval(timer);};
  }, [authed]);
  const downloadGroup = async d => {
    setBusyId(d.file_id);
    try {
      const {data}=await vaultApi.get(`/admin/document-groups/${encodeURIComponent(d.document_group_id || d.file_id)}/download`,{responseType:'blob',timeout:180000});
      const url=URL.createObjectURL(data); const anchor=document.createElement('a'); anchor.href=url; anchor.download=(d.document_title || 'document')+'.zip'; anchor.click(); setTimeout(()=>URL.revokeObjectURL(url),60000);
    } catch(e) {toast.error(formatApiError(e.response?.data?.detail,'Document download could not be prepared.'));}
    finally {setBusyId(null);}
  };

  const onSelect = (e) => {
    const valid = [];
    for (const f of Array.from(e.target.files || [])) {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED.includes(ext)) { toast.error(`${f.name}: unsupported file type.`); continue; }
      if (f.size > MAX_BYTES) { toast.error(`${f.name}: exceeds the 25 MB limit.`); continue; }
      valid.push(f);
    }
    if (valid.length > MAX_BATCH) toast.error(`Up to ${MAX_BATCH} files at a time. Keeping the first ${MAX_BATCH}.`);
    const selected = valid.slice(0, MAX_BATCH);
    if (selected.reduce((total, f) => total + f.size, 0) > MAX_BATCH_BYTES) {
      toast.error("This batch exceeds 150 MB. Select a smaller batch.");
      e.target.value = "";
      return;
    }
    setPending(selected);
    setQueue([]);
    e.target.value = "";
  };

  const upload = async () => {
    if (!pending.length || uploadLock.current) return;
    uploadLock.current = true;
    setUploading(true);
    setProgress({ completed: 0, total: pending.length });
    setQueue(pending.map(f => ({name: f.name, phase: "Queued", percent: 0})));
    const batchId = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `b_${Date.now()}`;
    let passed = 0;
    let quarantined = 0;
    let awaitingScan = 0;
    let duplicates = 0;
    const failed = [];
    let previousFileId = [...docs].sort((a, b) => a.created_at.localeCompare(b.created_at)).at(-1)?.file_id || null;
    try {
      for (const [index, f] of pending.entries()) {
        try {
          updateQueue(index, {phase: "Checking for duplicates", percent: 0});
          const result = await vaultUploadDocument(f, note, batchId, {previousFileId, onStage: phase => updateQueue(index, {phase, percent: 0}), onUploadProgress: event => {
            const percent = event.total ? Math.min(100, Math.round(event.loaded / event.total * 100)) : null;
            updateQueue(index, {phase: percent === 100 ? "Upload sent — scanning and storing" : "Uploading", percent});
          }});
          if (result.skipped_duplicate) {
            duplicates += 1;
            updateQueue(index, {phase: `Duplicate skipped — already stored as ${result.existing_title || result.existing_filename || f.name}`, percent: 100});
            previousFileId = result.malware_state === "SCAN_PASSED" ? result.file_id : null;
            continue;
          }
          updateQueue(index, {phase: result.malware_state === "QUARANTINED" ? "Quarantined — download blocked" : "Stored", percent: 100});
          if (result.malware_state === "SCAN_PASSED" && result.file_id) {
            updateQueue(index, {phase: "Stored · queued for background organization", percent: 100});
          }
          previousFileId = result.malware_state === "SCAN_PASSED" ? result.file_id : null;
          if (result.malware_state === "SCAN_PASSED") passed += 1;
          else if (result.malware_state === "QUARANTINED") quarantined += 1;
          else awaitingScan += 1;
        } catch (e) {
          previousFileId = null;
          failed.push(f);
          const error = formatApiError(e.response?.data?.detail, `${f.name}: upload failed. Please retry.`);
          updateQueue(index, {phase: "Upload failed — ready to retry", error, percent: 0}); toast.error(error);
        }
        finally { setProgress((p) => ({ ...p, completed: p.completed + 1 })); }
      }
      if (duplicates > 0) toast.success(`${duplicates} duplicate${duplicates > 1 ? "s" : ""} skipped. Existing records kept.`);
      if (passed > 0) toast.success(`${passed} document${passed > 1 ? "s" : ""} stored. Malware scan passed.`);
      if (quarantined > 0) toast.warning(`${quarantined} document${quarantined > 1 ? "s" : ""} quarantined. Download is blocked.`);
      if (awaitingScan > 0) toast.warning(`${awaitingScan} document${awaitingScan > 1 ? "s" : ""} awaiting a passed scan. Download is blocked.`);
      setPending(failed); if (!failed.length) setNote(""); await load();
    } finally { setUploading(false); uploadLock.current = false; }
  };

  const open = async (d) => {
    setBusyId(d.file_id);
    try {
      const blob = await vaultDownloadDocument(d.file_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename || "document";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not download document."));
    } finally { setBusyId(null); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.original_filename}" from the vault?`)) return;
    setBusyId(d.file_id);
    try {
      await vaultDeleteDocument(d.file_id);
      setDocs((p) => p.filter((x) => x.file_id !== d.file_id));
      toast.success("Document removed.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not remove document."));
    } finally { setBusyId(null); }
  };

  const doLogout = async () => { await vaultLogout(); setAuthed(false); };

  if (authed === null) return <div className="min-h-screen founder-theme" />;
  if (!authed) return <main className="founder-theme flex items-center justify-center px-6"><div><p role="alert">Your document session is temporarily unavailable.</p><button className="jmh-cta mt-4" onClick={connectVault}>Try again</button></div></main>;

  return (
    <div className="min-h-screen founder-theme px-6 py-12">
      <Seo title="JMH — Business Records Vault" />
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80">Jennae Michelle Holdings</p>
            <h1 className="mt-1 font-serif2 text-3xl text-[var(--bone)]">Business Records Vault</h1>
          </div>
          <button onClick={doLogout} className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="intake-logout">Sign out</button>
        </div>

        <p className="mt-4 text-sm text-[var(--muted)] leading-relaxed max-w-2xl">
          Private, custodian-managed records store. Every file is malware-scanned on receipt; only records that pass become downloadable. Up to {MAX_BATCH} files per upload; 25&nbsp;MB each and 150&nbsp;MB per batch.
        </p>
        <div className="mt-4 border border-[rgba(180,139,63,0.3)] bg-[rgba(180,139,63,0.05)] px-4 py-3 text-xs text-[var(--bone)]/70 max-w-2xl" data-testid="intake-scan-notice">
          <span className="font-display uppercase tracking-wide2 text-[10px] text-[var(--gold-lite)]">Hardened session · Malware-scanned</span>
          <span className="ml-2">A successful malware scan establishes only the scan result. It does not establish the document’s authenticity, signatures, ownership, or legal effect.</span>
        </div>

        <div className="corner-frame mt-8 px-6 md:px-8 py-8">
          <label className="block border border-dashed border-[rgba(180,139,63,0.35)] px-5 py-8 text-center cursor-pointer hover:border-[var(--gold)] transition-colors" data-testid="intake-drop">
            <input type="file" multiple disabled={uploading || organizing} accept=".pdf,.docx,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onSelect} data-testid="intake-file-input" />
            <span className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold-lite)]">
              {pending.length ? `${pending.length} file(s) ready` : "Select documents to upload"}
            </span>
            {pending.length > 0 && <p className="mt-2 text-xs text-[var(--muted)]">{pending.map((f) => f.name).join(", ")}</p>}
          </label>
          {pending.length > 0 && !uploading && !organizing && <p role="status" className="mt-3 text-[var(--gold-lite)]">{pending.length} file(s) ready. Press Upload to start.</p>}
          <input disabled={uploading || organizing} className={`${inputCls} mt-4`} placeholder="Optional note (e.g. 'Articles of Organization')" value={note} onChange={(e) => setNote(e.target.value)} data-testid="intake-note" />
          <div className="mt-5 flex items-center gap-3">
            <button onClick={upload} disabled={!pending.length || uploading || organizing} className="jmh-cta disabled:opacity-40" data-testid="intake-upload-btn">
              {uploading ? `Uploading & scanning ${progress.completed}/${progress.total}…` : `Upload ${pending.length || ""} to Vault`.trim()}
            </button>
            {pending.length > 0 && !uploading && !organizing && (
              <button onClick={() => setPending([])} className="font-display uppercase tracking-wide2 text-xs text-[var(--muted)] hover:text-[var(--gold-lite)]" data-testid="intake-clear-btn">Clear</button>
            )}
          </div>
        </div>

        {(uploading || organizing) && <div className="fixed bottom-0 inset-x-0 z-50 bg-black border-t border-[var(--gold)] px-5 py-4 flex items-center gap-3 shadow-xl" role="status" aria-live="polite">
          <svg className="h-6 w-6 animate-spin shrink-0 text-[var(--gold)]" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.25"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="3" fill="none"/></svg>
          <span className="flex-1">{uploading ? "Uploading and organizing" : "Organizing documents"} · {progress.completed}/{progress.total} processed</span>
          <button className="underline text-[var(--gold-lite)]" onClick={() => progressPanel.current?.scrollIntoView?.({behavior: "smooth", block: "start"})}>Show progress</button>
        </div>}
        {queue.length > 0 && <section ref={progressPanel} className="mt-6 mb-20 border border-[var(--gold)] rounded-xl p-5" data-testid="intake-upload-progress" aria-busy={uploading || organizing}>
          <h2 className="text-lg text-[var(--gold-lite)]" role="status" aria-live="polite">{uploading ? "Uploading, scanning and organizing" : organizing ? "Organizing stored documents" : "Batch finished"} · {progress.completed} of {progress.total} processed</h2>
          <progress className="w-full mt-3 accent-yellow-600" aria-label="Batch progress" max={progress.total || 1} value={progress.completed} />
          {(uploading || organizing) && <p className="text-sm mt-2">Keep this page open. Each file below shows its current step.</p>}
          <ul className="mt-3 space-y-3">{queue.map((row, index) => <li key={`${row.name}-${index}`} className="border-t border-[var(--gold)]/20 pt-2">
            <p className="break-all">{row.name}</p><p className="text-sm text-[var(--gold-lite)]">{row.phase}{row.phase === "Uploading" && row.percent !== null ? ` · ${row.percent}%` : ""}</p>
            {!["Queued"].includes(row.phase) && row.phase !== "Upload sent — scanning and storing" && row.percent !== null && <progress className="w-full accent-yellow-600" aria-label={`${row.name} progress`} value={row.percent} max={100} />}
            {row.error && <p className="text-sm text-red-300" role="alert">{row.error}</p>}
          </li>)}</ul>
        </section>}

        <DocumentInventory inventory={inventory} refresh={loadInventory} start={organizeExisting} busy={organizing || uploading} />

        <details className="mt-8 border border-[var(--gold)]/30 rounded-xl p-5"><summary className="cursor-pointer">Quick type checklist</summary>
          <h2 className="text-lg">Document checklist</h2>
          <select aria-label="Document checklist" value={checklist} onChange={e => saveChecklist(e.target.value, excluded)} className={`${inputCls} mt-3`}>
            {Object.keys(catalog.checklists || {}).map(name => <option className="bg-black" key={name}>{name}</option>)}
          </select>
          <p className="mt-2 text-xs text-[var(--muted)]">{catalog.note || "Starter organization checklist. Select what applies to your business or matter."} Found documents still require review.</p>
          <ul className="mt-3 space-y-2">{(catalog.checklists[checklist] || []).map(key => {
            const found = docs.filter(d => d.document_type === key && d.malware_state === "SCAN_PASSED" && ["AUTO_CATEGORIZED", "CONFIRMED"].includes(d.organization_state));
            const label = catalog.types.find(t => t.id === key)?.label || key;
            return <li key={key} className="flex flex-wrap justify-between gap-2 text-sm"><span>{label} · {excluded.includes(key) ? "Not applicable" : found.length ? `Found (${found.length})` : "Still needed"}</span><label><input type="checkbox" checked={excluded.includes(key)} onChange={e => saveChecklist(checklist, e.target.checked ? [...excluded, key] : excluded.filter(x => x !== key))} /> Not applicable</label></li>;
          })}</ul>
        </details>

        <CounselRelease onSaved={load} />
        <VaultRecovery />
        <div className="mt-10">
          <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-4">Stored Documents · {allGroups.length} documents · {docs.length} uploaded files</p>
          <button className="jmh-cta mb-4 disabled:opacity-40" onClick={organizeExisting} disabled={uploading || organizing || !docs.some(d => d.malware_state === "SCAN_PASSED" && d.organization_state !== "CONFIRMED")}>Organize existing documents</button>
          <select aria-label="Stored document business" className={`${inputCls} mb-3`} value={business} onChange={e=>setBusiness(e.target.value)}><option className="bg-black" value="all">All businesses</option>{(inventory?.entities || []).map(e=><option className="bg-black" key={e.id} value={e.id}>{e.name}</option>)}<option className="bg-black" value="unassigned">Unassigned entity</option></select>
          <input className={`${inputCls} mb-3`} placeholder="Search title, document, matter or type" aria-label="Search stored documents" value={search} onChange={e => setSearch(e.target.value)} />
          <div className="flex flex-wrap gap-2 mb-4" aria-label="Document folders">{["All documents", ...folders].map(name => <button key={name} onClick={() => setFolder(name)} aria-pressed={folder === name} className={`rounded-xl border px-3 py-2 text-sm ${folder === name ? "border-[var(--gold)] text-[var(--gold-lite)]" : "border-white/20"}`}>{name} ({name === "All documents" ? allGroups.length : allGroups.filter(g => g.search_pages.some(d => category(d) === name)).length})</button>)}</div>
          {loading ? (
            <div className="text-[var(--muted)] py-16 text-center">Loading…</div>
          ) : docs.length === 0 ? (
            <div className="text-[var(--muted)] py-16 text-center" data-testid="intake-empty">No documents stored yet.</div>
          ) : displayDocs.length === 0 ? <p role="status">No documents match this folder or search.</p> : (
            <ul className="space-y-3" data-testid="intake-list">
              {displayDocs.map((d, index) => (
                <li key={d.file_id} className="border border-[rgba(180,139,63,0.22)] px-5 py-4 flex items-center justify-between gap-4" data-testid={`intake-item-${d.file_id}`}>
                  <div className="min-w-0">
                    {(index === 0 || displayDocs[index-1].business_entity_id !== d.business_entity_id) && <p className="mb-3 text-lg text-[var(--gold-lite)] font-serif2">{d.business_entity_name || "Unassigned business"}</p>}
                    <p className="text-[var(--bone)] font-serif2 text-lg truncate">{d.document_title || d.original_filename}{d.grouped_pages.length > 1 ? ` · ${d.grouped_pages.length} pages` : d.document_title ? ` · Page ${d.page_number || 1}` : ""}</p>
                    {d.document_title && <p className="text-xs text-[var(--muted)]">Document starts at: {d.original_filename}{d.duplicate_copy_count > 0 ? ` · ${d.duplicate_copy_count} retained duplicate copies hidden` : ""}</p>}
                    <p className="text-xs text-[var(--muted)] truncate">{fmtSize(d.size)} · {fmtDate(d.created_at)}{d.note ? ` · ${d.note}` : ""}</p>
                    <span className="mt-1 inline-block font-display uppercase tracking-wide2 text-[9px] text-[var(--gold-lite)]/70" data-testid={`intake-scan-${d.file_id}`}>
                      {d.malware_label || "Pending malware scan"} · {d.sensitivity_class || "RESTRICTED_BUSINESS"}
                    </span>
                    <p className="mt-2 text-sm text-[var(--gold-lite)]">{category(d)} · {d.document_type_label || "Unidentified document"} · {d.organization_state === "CONFIRMED" ? "Founder confirmed" : d.organization_state === "AUTO_CATEGORIZED" ? "Automatically grouped and categorized" : "Needs review"}</p>
                    {d.organization_state === "NEEDS_REVIEW" && <p className="text-xs text-[var(--muted)]">{d.organization_reason}{d.suggested_category && d.suggested_category !== "Needs review" ? ` · Suggested: ${d.suggested_category}` : ""}</p>}
                    <p className="mt-2 text-sm">{d.business_entity_name || "Entity not established"}</p>
                    {d.record_path && <p className="text-xs text-[var(--muted)] break-words">{d.record_path}</p>}
                    {d.grouped_pages.length>1&&<details className="mt-3 text-sm"><summary className="cursor-pointer">View {d.grouped_pages.length} grouped pages</summary><ol className="mt-2 space-y-2">{d.grouped_pages.map(page=><li key={page.file_id}>Page {page.page_number || 1} · {page.original_filename} <button className="underline ml-2" disabled={page.malware_state!=="SCAN_PASSED"} onClick={()=>open(page)}>Download page</button></li>)}</ol></details>}
                    <details className="mt-3 space-y-2 text-sm"><summary className="cursor-pointer">Adjust organization / counsel release</summary>
                      <label>Document type<select className={inputCls} value={d.document_type || "unknown"} onChange={e => editOrganization(d.file_id, "document_type", e.target.value)}><option className="bg-black" value="unknown">Unidentified document</option>{catalog.types.map(t => <option className="bg-black" key={t.id} value={t.id}>{t.label}</option>)}</select></label>
                      <label>Matter / case<input className={inputCls} maxLength={160} value={d.legal_matter || "Business records"} onChange={(e) => editOrganization(d.file_id, "legal_matter", e.target.value)} /></label>
                      <label>Document category<input className={inputCls} maxLength={80} placeholder="Formation, contracts, evidence, correspondence…" value={d.legal_category || "Needs review"} onChange={(e) => editOrganization(d.file_id, "legal_category", e.target.value)} /></label>
                      <label className="block"><input type="checkbox" disabled={d.sensitivity_class === "TRUST_HIGHLY_RESTRICTED"} checked={!!d.legal_shared} onChange={(e) => editOrganization(d.file_id, "legal_shared", e.target.checked)} /> Release to lawyer portal</label>
                      <button onClick={() => organize(d)} disabled={busyId === d.file_id} className="underline disabled:opacity-40">Save organization and counsel access</button>
                    </details>
                  </div>
                  <div className="shrink-0 flex items-center gap-4">
                    <button onClick={() => d.grouped_pages.length>1 ? downloadGroup(d) : open(d)} disabled={busyId === d.file_id || d.malware_state !== "SCAN_PASSED"} title={d.malware_state !== "SCAN_PASSED" ? "Available after malware scan passes" : ""} className="font-display uppercase tracking-wide2 text-[10px] text-[var(--gold-lite)] hover:text-[var(--gold)] disabled:opacity-40" data-testid={`intake-open-${d.file_id}`}>{d.grouped_pages.length>1?"Download document":"Download"}</button>
                    <button onClick={() => remove(d)} disabled={busyId === d.file_id} className="font-display uppercase tracking-wide2 text-[10px] text-[var(--muted)] hover:text-red-400 disabled:opacity-40" data-testid={`intake-delete-${d.file_id}`}>Remove</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
